import { Button } from "@/components";
import { PublicRoutes } from "@/constants/routes";
import { Alert, Button as DsButton } from "@/design-system";
import { useForgotPassword, useSendSecurityCode } from "@/domain/auth";
import { useToast } from "@/hooks";
import { cn, phoneUtils } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

const CODE_LENGTH = 6;
const CODE_TTL_SECONDS = 5 * 60;
const RESEND_COOLDOWN_SECONDS = 60;

const formatTime = (seconds: number): string => {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
};

export const ForgotPasswordCode = () => {
  const [params] = useSearchParams();
  const phone = params.get("phone") ?? "";

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { sendSecurityCode, isSendingSecurityCode } = useSendSecurityCode({
    phone,
  });
  const { sendPhone, isPending: isResending } = useForgotPassword();

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [remaining, setRemaining] = useState(CODE_TTL_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const code = digits.join("");
  const isComplete = code.length === CODE_LENGTH;
  const isExpired = remaining <= 0;
  const canResend = resendCooldown <= 0 && !isResending;

  // Sem telefone na query não há como validar — volta ao início do fluxo.
  useEffect(() => {
    if (!phone) navigate(PublicRoutes.AUTH_FORGOT_PASSWORD_PHONE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contador regressivo de 5 minutos (validade do código).
  useEffect(() => {
    if (remaining <= 0) return;
    const id = setInterval(() => setRemaining(prev => prev - 1), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  // Cooldown de 60s entre reenvios.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const resetDigits = () => {
    setDigits(Array(CODE_LENGTH).fill(""));
    inputsRef.current[0]?.focus();
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = event.target.value;
    if (!/^\d?$/.test(value)) return;

    setDigits(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  // Permite colar o código completo em qualquer um dos campos.
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;

    event.preventDefault();
    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((digit, i) => (next[i] = digit));
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleValidate();
  };

  // Valida o código na hora e, em caso de sucesso, leva para a nova senha
  // carregando telefone + código na query.
  const handleValidate = () => {
    sendSecurityCode(code, {
      onSuccess: () => {
        navigate(
          `${PublicRoutes.AUTH_FORGOT_PASSWORD_RESET}?phone=${phone}&code=${code}`
        );
      },
      onError: error => {
        showToast(error.message, "error");
        // Código expirado → libera o reenvio zerando o contador.
        if (error.message.toLowerCase().includes("expirad")) {
          setRemaining(0);
        }
        resetDigits();
      },
    });
  };

  const handleResend = () => {
    sendPhone(
      { phone },
      {
        onSuccess: () => {
          // The API answers the same with or without an account: do not claim a send.
          showToast("Pedido de reenvio registrado", "success");
          setRemaining(CODE_TTL_SECONDS);
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
          resetDigits();
        },
        onError: error => showToast(error.message, "error"),
      }
    );
  };

  const maskedPhone = phoneUtils.formatPhone(phone);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-black">Confirmar código</h1>
        <p className="text-base text-gray-500">
          Digite o código de 6 dígitos recebido no WhatsApp{" "}
          <span className="font-medium text-black">{maskedPhone}</span>.
        </p>
      </div>

      <Alert
        tone="info"
        action={
          <DsButton
            size="sm"
            variant="secondary"
            navigates="internal"
            onClick={() => navigate(PublicRoutes.AUTH_FORGOT_PASSWORD_PHONE)}
          >
            Alterar número
          </DsButton>
        }
      >
        Se este número estiver cadastrado na Fidexa, o código chega pelo
        WhatsApp em instantes.
      </Alert>

      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <div className="flex justify-between gap-2">
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            <input
              key={index}
              ref={el => {
                if (el) inputsRef.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              autoFocus={index === 0}
              value={digits[index]}
              onChange={event => handleChange(event, index)}
              onKeyDown={event => handleKeyDown(event, index)}
              onPaste={handlePaste}
              className="h-12 w-full rounded-lg border border-gray-200 text-center text-lg font-bold text-black outline-none focus:border-primary"
            />
          ))}
        </div>

        <Button
          type="submit"
          isLoading={isSendingSecurityCode}
          disabled={!isComplete || isExpired || isSendingSecurityCode}
        >
          Confirmar código
        </Button>

        <div className="flex flex-col items-center gap-1 text-center">
          {!isExpired ? (
            <span className="text-sm text-gray-500">
              O código expira em {formatTime(remaining)}
            </span>
          ) : (
            <span className="text-sm text-red">
              Código expirado. Solicite um novo.
            </span>
          )}
          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend}
            className={cn(
              "cursor-pointer text-sm font-medium text-primary hover:underline",
              !canResend &&
                "cursor-default text-gray-400 no-underline hover:no-underline"
            )}
          >
            {resendCooldown > 0
              ? `Reenviar código em ${formatTime(resendCooldown)}`
              : "Reenviar código via WhatsApp"}
          </button>
        </div>
      </form>
    </div>
  );
};
